import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import { openExternalUrl } from "@/shared/openExternal";
import { Button, RISHFANS, contrastFg } from "@/shared/ui";

import { ESPACE_RISHFANS } from "./demenagementRishfans";

const IS_IN_TAURI =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

/** Le temps de lire l'écran avant d'être emmené. */
const SECONDES_AVANT_DEPART = 10;

/**
 * L'écran de l'affilié qui se connecte au cockpit : son espace est sur
 * app.rishfans.com (cf `demenagementRishfans.ts`). Il dit où aller et comment
 * s'y connecter, puis l'y emmène. Noir et or, comme RishFans : le thème sombre est reposé sur
 * l'écran (`.dark`) et la marque prend l'accent de la marque RishFans de la librairie — aucune
 * couleur écrite ici, les jetons font le reste.
 *
 * Dans l'app desktop (webview Tauri), pas de départ automatique : la webview ne
 * navigue que sur dozka.app, le bouton ouvre le navigateur.
 */
export function DemenagementVersRishfans() {
  const { t } = useTranslation();
  const [reste, setReste] = useState(SECONDES_AVANT_DEPART);

  useEffect(() => {
    if (IS_IN_TAURI) return;
    if (reste <= 0) {
      window.location.replace(ESPACE_RISHFANS);
      return;
    }
    const id = window.setTimeout(() => setReste((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [reste]);

  const partir = () => {
    if (IS_IN_TAURI) void openExternalUrl(ESPACE_RISHFANS);
    else window.location.replace(ESPACE_RISHFANS);
  };

  return (
    <div
      className="dark flex h-full w-full flex-col items-center justify-center gap-6 bg-canvas px-8 py-12 text-center text-fg"
      data-theme="dark"
      style={
        RISHFANS.accent
          ? ({ "--color-mark": RISHFANS.accent, "--color-mark-fg": contrastFg(RISHFANS.accent) } as CSSProperties)
          : undefined
      }
    >
      <img src="/rishfans/logo.png" alt="RishFans" className="h-14 w-auto" />

      <div className="flex max-w-md flex-col gap-3">
        <h1 className="text-xl font-semibold">{t("shell.demenagementRishfans.title")}</h1>
        <p className="text-sm leading-relaxed text-muted">
          {t("shell.demenagementRishfans.subtitle")}
        </p>
        <p className="text-sm leading-relaxed text-muted">
          {t("shell.demenagementRishfans.connexion")}
        </p>
      </div>

      <Button variant="accent" size="xl" onClick={partir}>
        {t("shell.demenagementRishfans.bouton")}
      </Button>

      {!IS_IN_TAURI && (
        <p className="text-xs text-faint">
          {t("shell.demenagementRishfans.depart", { n: reste })}
        </p>
      )}
    </div>
  );
}
